// client/src/pages/Game/Battlefield/Battlefield.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useGame } from '../../../hooks/useGame';
import { useNotification } from '../../../hooks/useNotification';
import gameNotificationService from '../../../services/gameNotificationService';
import { AudioContext } from '../../../contexts/AudioContext';
import { useAudio } from '../../../hooks/useAudio';
import socketService from '../../../api/socketService';
import { useAuth } from '../../../hooks/useAuth';

// Composants
import GameLog from './components/GameLog';
import PlayerHand from './components/PlayerHand';
import MonsterZone from './components/MonsterZone';
import DeckDisplay from './components/DeckDisplay';
import TerrainSlot from './components/TerrainSlot';
import StartVortex from './components/StartVortex';
import { animateFlight } from './utils/cardFlight';
import EnhancedLifePoints from './components/EnhancedLifePoints';
import ActionButton from './components/ActionButton';
import DevPanel from './components/DevPanel';
import SettingsMenu from './components/SettingsMenu';
import GameResult from './components/GameResult';
import DuelCoach from '../../../components/tutorial/DuelCoach';
import TurnTimerIndicator from './components/TurnTimerIndicator';
import DeckSelector from './components/DeckSelector';
import ResumeScreen from './components/ResumeScreen';
import LoadingScreen from './components/LoadingScreen';
import BattleZone from './components/BattleZone';
import MulliganScreen from './components/MulliganScreen';
import RpsScreen from './components/RpsScreen';
import BattlefieldCardPreview from './components/BattlefieldCardPreview';

// Hooks personnalisés
import useBattlefieldDragDrop from './hooks/useBattlefieldDragDrop';
import useDevMode from './hooks/useDevMode';
import useGameElements from './hooks/useGameElements';
import useCombatAnimations from './hooks/useCombatAnimations';
import { flyCardToElement } from './utils/cardFlight';

// Assets
import './Battlefield.css';
import './components/MulliganScreen.css';
import './components/RpsScreen.css';
import battleBackground from '../../../assets/images/battleBoardScreen.png';
import victoryImage from '../../../assets/images/victoryScreen.png';
import defeatImage from '../../../assets/images/defeatScreen.jpg';
import battleboardVideo from '../../../assets/videos/battleboard_background1.mp4';

/**
 * Détermine le numéro de tour à afficher, en tenant compte des incohérences possibles
 * @param {Object} gameState - L'état actuel du jeu
 * @returns {Number} - Le numéro de tour à afficher
 */
const determineVisibleTurn = (gameState) => {
  if (!gameState) return 1;
  
  // Normalement, le tour et l'énergie maximale devraient être identiques (jusqu'à 10)
  // Si la différence est supérieure à 1, quelque chose ne va pas
  if (gameState.turn > gameState.playerMaxEnergy + 1) {
    return gameState.playerMaxEnergy; // Utiliser l'énergie comme référence
  }
  
  // Si nous sommes en début de tour (l'énergie a été mise à jour mais le tour aussi)
  if (gameState.playerMaxEnergy === gameState.turn && gameState.activePlayer === 'player') {
    return gameState.turn;
  }
  
  // Si nous sommes en fin de tour (l'énergie n'a pas encore été mise à jour)
  if (gameState.playerMaxEnergy === gameState.turn - 1) {
    return gameState.playerMaxEnergy;
  }
  
  // Cas par défaut
  return gameState.turn;
};

/**
 * Composant principal de l'écran de bataille
 * @returns {JSX.Element} Le composant Battlefield
 */
const Battlefield = () => {
  const { gameId } = useParams();
  const [tutorialSearchParams] = useSearchParams();
  const isTutorialDuel = tutorialSearchParams.get('tutorial') === '1';
  const navigate = useNavigate();
  const battlefieldRef = useRef(null);
  
  // Hooks d'état du jeu
  const { 
    currentGame, 
    gameState, 
    joinExistingGame, 
    createNewGame,
    resumeActiveGame,
  hasActiveGame,
    leaveGame, 
    loading: gameLoading, 
    error: gameError,
    isAIGame,
    isOfflineMode,
    refreshGameState,
    performGameAction,
    gameRewards
  } = useGame();
  
  const { error: showError, success: showSuccess } = useNotification();
  const { user: authUser } = useAuth();
  const isAdminUser = !!authUser?.isAdmin;

  // R34s : déplacement individuel d'une gemme PV (mode dev) — position
  // absolue stockée dans size.gems[gemId] (cqw depuis le centre du cercle).
  const makeGemMoveHandler = (lifeKey) => (gemId, pos) => {
    setElementSizes((prev) => {
      const cur = prev[lifeKey] || {};
      const gems = { ...(cur.gems || {}), [gemId]: { x: +pos.x.toFixed(2), y: +pos.y.toFixed(2) } };
      const nextSizes = { ...prev, [lifeKey]: { ...cur, gems } };
      try { localStorage.setItem('elementSizes_pct', JSON.stringify(nextSizes)); } catch (e) {}
      return nextSizes;
    });
  };
  const { pauseMusic, playMusic, volume } = React.useContext(AudioContext);

  // R34q : la purge serveur (inactivité) émet 'game-expired' — avant, la
  // partie disparaissait silencieusement. On prévient le joueur et on le
  // renvoie au matchmaking.
  useEffect(() => {
    const onExpired = (data) => {
      if (!data || !data.gameId) return;
      if (data.gameId !== currentGame) return;
      showError('Partie supprimée pour inactivité — retour au matchmaking');
      try { leaveGame(); } catch (e) { /* déjà nettoyé */ }
      navigate('/game/matchmaking', { replace: true });
    };
    socketService.onGameExpired(onExpired);
    return () => socketService.onGameExpired(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame]);
  
  // États locaux
  const [phaseChanging, setPhaseChanging] = useState(false);
  const [showGameLog, setShowGameLog] = useState(false);
  const [graveyardView, setGraveyardView] = useState(null);
  const [gyIndex, setGyIndex] = useState(0);
  const [startVortexDone, setStartVortexDone] = useState(false);
  // R34dd : deck mémorisé — pas de re-sélection à chaque partie
  const [selectedDeckId, setSelectedDeckId] = useState(() => {
    try { return localStorage.getItem('elementum_preferred_deck') || 'starter_fire'; }
    catch (e) { return 'starter_fire'; }
  });
  const [gameResult, setGameResult] = useState(null);
  const [showDeckSelector, setShowDeckSelector] = useState(false);
  // R27 : écran « partie en cours » après refresh (avant le sélecteur de deck)
  const [showResumeScreen, setShowResumeScreen] = useState(false);
  const [resumeError, setResumeError] = useState(null);
  const [isRejoining, setIsRejoining] = useState(false);
  // R27 : mode ciblage d'une capacité à activation manuelle
  // { card, abilityIndex, scope: 'enemy' | 'ally' | 'any' }
  const [abilityTargeting, setAbilityTargeting] = useState(null);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const [serverConnectAttempts, setServerConnectAttempts] = useState(0);
  const [connectionError, setConnectionError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showMulligan, setShowMulligan] = useState(false);
  // Signature de la dernière action notifiée (évite les toasts en double
  // quand l'état est re-diffusé avec le même lastAction)
  const lastNotifiedActionRef = useRef(null);
  // Index du dernier log déjà converti en toast (le log est append-only :
  // on ne notifie que les NOUVELLES entrées, jamais l'historique au montage)
  const lastLogIndexRef = useRef(null);
  // Le shi-fu-mi reste affiché ~2,4s après le résultat pour l'animation
  // de révélation, puis laisse place au mulligan
  const [rpsDismissed, setRpsDismissed] = useState(false);
  const rpsDismissedSigRef = useRef(null);
  const { startBattleMusic, currentMusicSource } = useAudio();
  
  // Hook pour gérer les positions et tailles des éléments
  const { 
    elementPositions, 
    setElementPositions, 
    elementSizes, 
    setElementSizes,
    configLoaded,
    saveToLocal,
    fontScale,
    updateGlobalFontScale,
    fontScales,
    updateElementFontScale,
    resetFontScales
  } = useGameElements();
  
  // Hook pour le mode développeur
  const {
    devMode,
    toggleDevMode,
    draggingElement,
    adminSecret,
    updateAdminSecret,
    handleDevElementDragStart,
    handleDevElementDragEnd,
    resetElementPositions,
    updateElementPosition,
    updateElementSize,
    isDevPanelCollapsed,
    setIsDevPanelCollapsed,
    devPanelPosition,
    isDraggingDevPanel,
    handleDevPanelDragStart,
    handleDevPanelDrag,
    handleDevPanelDragEnd,
    devPanelRef,
    setupDevDragHandlers
  } = useDevMode(localStorage.getItem('devMode') === 'true');
  
  // Hook pour le drag and drop des cartes
  // NOTE : plus de paramètre devMode — le drag de cartes est du gameplay
  // et reste actif en mode dev (les zones, elles, restent déplaçables).
  const {
    draggingCard,
    handleCardDragStart,
    handleCardDragEnd,
    handleCardDrop,       // Fonction pour déposer une carte dans une zone
    handleAttack,         // Fonction pour attaquer avec une carte
    handleCardClick,
    setupCardDragHandlers,
    getCardElement,
    suppressNextClickRef
  } = useBattlefieldDragDrop(gameState, performGameAction, refreshGameState);

  // Animations de combat : chocs, dégâts, destructions, sorts
  useCombatAnimations(gameState, getCardElement);
  
  // Ouvrir l'aperçu d'une carte — sauf juste après un drag :
  // l'événement click arrive TOUJOURS après mouseup/touchend, il ne faut
  // donc pas ouvrir l'aperçu quand on vient de relâcher une carte.
  const handleSelectCard = useCallback((card) => {
    if (suppressNextClickRef.current) return;
    // R27 : mode ciblage d'effet activé — la carte cliquée est la cible
    if (abilityTargeting) {
      const t = abilityTargeting;
      const isOwn = (gameState?.playerField || []).some(c => c.instanceId === card.instanceId);
      const scopeOk = t.scope === 'any' || (t.scope === 'enemy' && !isOwn) || (t.scope === 'ally' && isOwn);
      if (card.zone === 'monster' && scopeOk) {
        setAbilityTargeting(null);
        performGameAction('ACTIVATE_ABILITY', {
          cardInstanceId: t.card.instanceId,
          abilityIndex: t.abilityIndex,
          targetInstanceId: card.instanceId,
        });
      } else {
        showError('Cible invalide pour cet effet');
      }
      return;
    }
    setSelectedCard(card);
  }, [suppressNextClickRef, abilityTargeting, gameState, performGameAction, showError]);
  
  // Ajoutez cet effet pour gérer la musique
  useEffect(() => {
    console.log("[Battlefield] Chargé, démarrage de la musique de bataille");
    
    // Démarrer la musique de bataille avec un léger délai pour éviter les problèmes de transition
    setTimeout(() => {
      startBattleMusic();
    }, 500);
    
    // Nettoyage: ne rien faire car la musique sera gérée par où l'utilisateur navigue ensuite
    return () => {
      // Si nécessaire, on pourrait remettre la musique du dashboard ici,
      // mais cela pourrait causer d'autres problèmes
    };
  }, [startBattleMusic]);
  
  // R34aa : partie perdue côté serveur → message + retour propre à l'accueil
  useEffect(() => {
    const onGameLost = () => {
      try { showError && showError('Partie introuvable côté serveur — retour à l\'accueil.'); } catch (e) {}
      navigate('/', { replace: true });
    };
    window.addEventListener('elementum:game-lost', onGameLost);
    return () => window.removeEventListener('elementum:game-lost', onGameLost);
  }, [navigate, showError]);

  // Pour debogage, vous pouvez ajouter cet effet
  useEffect(() => {
    console.log("[Battlefield] État de la musique:", currentMusicSource);
  }, [currentMusicSource]);

  // Effet pour détecter les environnements spéciaux (mobile, Telegram, etc.)
  useEffect(() => {
    // Détection des environnements spéciaux
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isTelegram = window.Telegram && window.Telegram.WebApp;
    
    console.log("[Battlefield] Détection environnement:", {
      isMobile, 
      isTelegram, 
      userAgent: navigator.userAgent
    });
    
    if (isMobile || isTelegram) {
      // Ajuster le comportement touch pour les environnements mobiles
      document.addEventListener('touchmove', function(e) {
        if (e.target.closest('.monster-card')) {
          // Ne pas bloquer le scroll global sauf pendant le drag
          if (draggingCard) e.preventDefault();
        }
      }, { passive: false });
      
      // Style spécifique pour améliorer le touch pour Telegram
      if (isTelegram) {
        const style = document.createElement('style');
        style.textContent = `
          .battle-zone {
            z-index: 100 !important;
            touch-action: none !important;
          }
          .monster-card {
            touch-action: none !important;
          }
          
          /* Animation plus visible pour les zones de drop valides */
          .battle-zone.active-drop-target {
            border: 3px dashed rgba(0, 255, 0, 0.8) !important;
            background-color: rgba(0, 255, 0, 0.15) !important;
            box-shadow: 0 0 20px rgba(0, 255, 0, 0.7) !important;
            animation: drop-target-pulse 1.5s infinite !important;
          }
          
          @keyframes drop-target-pulse {
            0% { opacity: 0.7; }
            50% { opacity: 1; }
            100% { opacity: 0.7; }
          }
          
          /* Style amélioré pour la carte en cours de drag */
          .dragging-card {
            transform: scale(1.1);
            box-shadow: 0 0 25px rgba(255, 255, 255, 0.8) !important;
            border: 2px solid white;
            animation: drag-pulse 1.2s infinite alternate;
          }
          
          @keyframes drag-pulse {
            0% { box-shadow: 0 0 15px rgba(255, 255, 255, 0.6); }
            100% { box-shadow: 0 0 25px rgba(255, 255, 255, 0.9); }
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, [draggingCard]);
  
  // Effet pour détecter quand afficher la phase de mulligan
  useEffect(() => {
    if (gameState && gameState.phase === 'mulligan' && !showMulligan) {
      setShowMulligan(true);
    } else if (gameState && gameState.phase !== 'mulligan' && showMulligan) {
      setShowMulligan(false);
    }
  }, [gameState, showMulligan]);
  
  // Fonction pour gérer la validation du mulligan (remplacement de cartes initiales)
  const handleMulliganConfirm = async (selectedCardIds) => {
    try {
      setPhaseChanging(true);
      console.log("[BATTLEFIELD] Confirmation du mulligan avec cartes:", selectedCardIds);
      
      const result = await performGameAction('CONFIRM_MULLIGAN', {
        selectedCards: selectedCardIds
      });
      
      if (!result || !result.gameState) {
        await refreshGameState();
      }
      
      setShowMulligan(false);
    } catch (error) {
      console.error("Erreur lors du mulligan:", error);
      showError("Erreur lors du mulligan: " + (error.message || "Erreur inconnue"));
    } finally {
      setPhaseChanging(false);
    }
  };

  // Pour le débogage dans Telegram WebApp
  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      console.log("[Battlefield] Telegram WebApp détecté");
      
      // Ajouter des logs pour déboguer
      const originalConsoleLog = console.log;
      console.log = function(...args) {
        originalConsoleLog.apply(console, args);
        // Afficher les logs dans un élément visible si nécessaire
        if (args[0]?.toString().includes("[Battlefield]") || 
            args[0]?.toString().includes("[BattleZone]") ||
            args[0]?.toString().includes("[MonsterZone]")) {
          const logDiv = document.querySelector('#debug-log') || 
                        (() => {
                          const div = document.createElement('div');
                          div.id = 'debug-log';
                          div.style.position = 'fixed';
                          div.style.bottom = '0';
                          div.style.left = '0';
                          div.style.zIndex = '9999';
                          div.style.backgroundColor = 'rgba(0,0,0,0.7)';
                          div.style.color = 'white';
                          div.style.padding = '5px';
                          div.style.fontSize = '10px';
                          div.style.maxHeight = '100px';
                          div.style.overflow = 'auto';
                          document.body.appendChild(div);
                          return div;
                        })();
          logDiv.innerHTML += `<div>${args.join(' ')}</div>`;
        }
      };
    }
  }, []);

  // Mode plein écran
  useEffect(() => {
    const enableFullscreenMode = () => {
      document.body.classList.add('battlefield-fullscreen');
      
      // Cacher l'en-tête et le pied de page si présents
      const header = document.querySelector('header');
      const footer = document.querySelector('footer');
      const navbar = document.querySelector('nav');
      
      if (header) header.style.display = 'none';
      if (footer) footer.style.display = 'none';
      if (navbar) navbar.style.display = 'none';
      
      // Empêcher le scroll
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    };
    
    // Fonction pour quitter le plein écran
    const disableFullscreenMode = () => {
      document.body.classList.remove('battlefield-fullscreen');
      
      // Restaurer l'en-tête et le pied de page
      const header = document.querySelector('header');
      const footer = document.querySelector('footer');
      const navbar = document.querySelector('nav');
      
      if (header) header.style.display = '';
      if (footer) footer.style.display = '';
      if (navbar) navbar.style.display = '';
      
      // Restaurer le scroll
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
    
    // Activer le mode plein écran
    enableFullscreenMode();
    
    // Nettoyage: restaurer l'état normal lorsque le composant est démonté
    return () => {
      disableFullscreenMode();
    };
  }, []);
  
  useEffect(() => {
    // Fonction pour surveiller le début d'un drag
    const monitorDragStart = (e) => {
      if (e.target.matches('.monster-card')) {
        console.log("[MONITOR] Drag start détecté sur une carte monstre");
      }
    };
    
    // Fonction pour surveiller un drop
    const monitorDrop = (e) => {
      console.log("[MONITOR] Drop détecté à la position", e.clientX, e.clientY);
      console.log("[MONITOR] Éléments sous le curseur:", 
        document.elementsFromPoint(e.clientX, e.clientY)
          .map(el => el.className)
          .join(', ')
      );
    };
    
    // Ajout des écouteurs
    document.addEventListener('dragstart', monitorDragStart);
    document.addEventListener('drop', monitorDrop);
    
    // Nettoyage
    return () => {
      document.removeEventListener('dragstart', monitorDragStart);
      document.removeEventListener('drop', monitorDrop);
    };
  }, []);
  
  // Configurer les gestionnaires d'événements de drag and drop
  // Le drag de CARTES (gameplay) est TOUJOURS actif.
  // Le drag d'ÉLÉMENTS (positionnement dev) s'ajoute par-dessus en mode dev —
  // les zones déplaçables n'interceptent pas les cartes (stopPropagation).
  useEffect(() => {
    if (!battlefieldRef.current) return;
    
    const cleanups = [];
    
    // 1. Drag des cartes — gameplay, prioritaire, toujours attaché
    cleanups.push(setupCardDragHandlers(battlefieldRef.current).cleanup);
    
    // 2. Drag des éléments — positionnement, uniquement en mode dev
    if (devMode) {
      cleanups.push(setupDevDragHandlers(
        battlefieldRef.current,
        elementPositions,
        setElementPositions,
        elementSizes,
        saveToLocal
      ).cleanup);
    }
  
    return () => cleanups.forEach(fn => fn());
  }, [
    devMode, 
    setupDevDragHandlers, 
    setupCardDragHandlers, 
    elementPositions, 
    setElementPositions,
    elementSizes,
    saveToLocal
  ]);
  
  // ── Animation des cartes jouées par l'ADVERSAIRE ──
  // On détecte les nouvelles cartes / changements de zone côté adverse et on
  // fait voler la carte depuis sa main jusqu'à sa destination (zone monstres
  // ou zone de combat pour une attaque).
  const oppFlightInitRef = useRef(false);
  const prevOppFieldRef = useRef([]);
  useEffect(() => {
    const field = gameState?.opponentField;
    if (!Array.isArray(field)) return;
    
    // Ignorer le premier rendu (chargement de partie en cours)
    if (!oppFlightInitRef.current) {
      oppFlightInitRef.current = true;
      prevOppFieldRef.current = field;
      return;
    }
    
    const prev = prevOppFieldRef.current || [];
    const keyOf = (c) => c.instanceId || c.id;
    const prevMap = new Map(prev.map(c => [keyOf(c), c]));
    
    field.forEach(card => {
      const old = prevMap.get(keyOf(card));
      
      if (!old && card.zone === 'monster') {
        // Carte jouée depuis la main → vol main adverse → zone monstres
        console.log('[Battlefield] Animation: adversaire joue', card.name);
        const handEl = document.querySelector('.opponent-hand');
        const targetEl = document.querySelector(
          `.opponent-monsters .monster-card[data-position="${card.position}"]`
        ) || document.querySelector('.opponent-monsters');
        
        if (handEl && targetEl) {
          const r = handEl.getBoundingClientRect();
          flyCardToElement({
            card,
            getCardElement,
            from: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
            targetEl,
            duration: 600,
          });
        }
      } else if (old && old.zone !== 'battle' && card.zone === 'battle' && false) {
        // R34dd : désactivé — la déclaration monstre→combat est déjà animée
        // par useCombatAnimations §1 (flyCloneToCard). Ce vol simultané
        // créait un doublon partant dans l'autre sens.
        // Monstre adverse qui attaque → vol zone monstres → zone de combat
        console.log('[Battlefield] Animation: adversaire attaque avec', card.name);
        const targetEl = document.querySelector(
          `.opponent-battle-zone .battle-monster-card[data-position="${card.position}"]`
        ) || document.querySelector('.opponent-battle-zone');
        const srcEl = document.querySelector(
          `.opponent-monsters .monster-card[data-position="${old.position}"]`
        );
        
        if (targetEl) {
          const r = srcEl?.getBoundingClientRect()
            || document.querySelector('.opponent-monsters')?.getBoundingClientRect();
          if (r) {
            flyCardToElement({
              card,
              getCardElement,
              from: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
              targetEl,
              duration: 480,
            });
          }
        }
      }
    });
    
    prevOppFieldRef.current = field;
  }, [gameState?.opponentField, getCardElement]);
  
  // Détermine si on montre le sélecteur de deck
  // (après avoir tenté de reprendre une partie en cours — refresh/F5)
  const [resumeAttempted, setResumeAttempted] = useState(false);
  // R34f6 : attachement en cours à une partie ciblée par l'URL
  const [attaching, setAttaching] = useState(false);
  
  // Tentative de reprise de la partie persistée (une seule fois au montage)
  useEffect(() => {
    if (resumeAttempted) return;
    setResumeAttempted(true);
    
    if (!currentGame && !gameState) {
      // R34f6 : l'URL cible une partie précise (match trouvé, lobby, lien)
      // → on s'attache DIRECTEMENT à la partie. Jamais de sélecteur de deck.
      if (gameId && gameId !== 'ai') {
        console.log("[BATTLEFIELD] Partie ciblée par l'URL — attachement:", gameId);
        try {
          sessionStorage.setItem('elementum_active_game', JSON.stringify({ gameId, at: Date.now() }));
        } catch (e) { /* non bloquant */ }
        setAttaching(true);
        resumeActiveGame()
          .then((ok) => {
            setAttaching(false);
            if (!ok) {
              console.warn("[BATTLEFIELD] Attachement impossible — retour au matchmaking");
              navigate('/game/matchmaking');
            }
          })
          .catch(() => { setAttaching(false); navigate('/game/matchmaking'); });
      } else if (hasActiveGame && hasActiveGame()) {
        // R27 : proposer explicitement de reprendre ou d'abandonner
        // (plus de sélecteur de deck imposteur au milieu d'un duel)
        console.log("[BATTLEFIELD] Partie persistée détectée — écran de reprise");
        setShowResumeScreen(true);
      } else {
        // R34f6 : plus AUCUN sélecteur de deck — retour au matchmaking
        navigate('/game/matchmaking');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    // Nettoyer le flag pour éviter les doubles rendus
    let isMounted = true;
    
    const updateDeckSelectorVisibility = () => {
      if (!isMounted) return;
      
      // Cas 1: Route AI sans partie active -> montrer sélecteur
      // (seulement après l'échec d'une éventuelle reprise)
      if (!showResumeScreen && gameId === 'ai' && !currentGame && !isStartingGame && !gameState && resumeAttempted && !showDeckSelector && !attaching) {
        console.log("[BATTLEFIELD] Route AI sans partie active, affichage du sélecteur");
        setShowDeckSelector(true);
      }
      // Cas 3: État de jeu disponible → masquer sélecteur
      else if (gameState && showDeckSelector) {
        console.log("[BATTLEFIELD] État de jeu disponible, fermeture du sélecteur");
        setShowDeckSelector(false);
      }
    };
    
    // Exécuter une seule fois par cycle de rendu
    updateDeckSelectorVisibility();
    
    return () => { isMounted = false; };
  }, [gameId, currentGame, gameState, showDeckSelector, isStartingGame, resumeAttempted, showResumeScreen]);

  // Détection de fin de partie
  // On utilise isGameOver du serveur (source de vérité) au lieu de vérifier nous-même
  // car opponentDeck est vidé par sanitizeStateForPlayer (les cartes sont cachées au client)
  // R34bb : (1) ignorer les états sans partie active (broadcast tardif)
  //         (2) attendre la FIN des animations de combat avant d'afficher l'écran
  useEffect(() => {
    if (!currentGame) return;
    if (gameState && gameState.isGameOver) {
      const playerWon = gameState.winner === 'player';
      const reason = 
        gameState.playerLife <= 0 ? 'PV épuisés' :
        gameState.opponentLife <= 0 ? 'PV épuisés' :
        (gameState.playerDeck && gameState.playerDeck.length === 0) ? 'Deck épuisé' :
        (gameState.opponentDeckCount !== undefined && gameState.opponentDeckCount === 0) ? 'Deck épuisé' :
        'Partie terminée';
      // Laisser se jouer les animations de résolution (chocs, morts, retours
      // groupés ~2,3 s) avant de recouvrir le terrain.
      const t = setTimeout(() => {
        setGameResult({
          winner: playerWon ? 'player' : 'opponent',
          reason
        });
      }, 3200);
      return () => clearTimeout(t);
    } else if (gameState && !gameState.isGameOver && gameResult) {
      // Reset game result when a new game starts
      setGameResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, currentGame]);

  // Gestion des notifications
  useEffect(() => {
    if (gameState?.lastAction) {
      const { type, player: actionPlayer } = gameState.lastAction;
      const byOpponent = actionPlayer === 'opponent';

      // Anti-doublon : les broadcasts cadencés de l'IA et les refresh d'état
      // renvoient le même lastAction → on ne notifie qu'une fois par action.
      const signature = `${type}|${actionPlayer}|${gameState.lastAction.message}`;
      if (lastNotifiedActionRef.current === signature) return;
      lastNotifiedActionRef.current = signature;

      // lastAction.card est soit une string (chemin IA), soit un objet
      // { id, name, ... } (chemin humain/GameEngine) — on extrait toujours un nom.
      const rawCard = gameState.lastAction.card;
      const cardName = typeof rawCard === 'string'
        ? rawCard
        : (rawCard && rawCard.name) || '';

      switch (type) {
        case 'BATTLE_VICTORY':
        case 'BATTLE_DEFEAT':
        case 'DIRECT_ATTACK':
          if (gameState.lastAction.damage && gameState.lastAction.attacker) {
            gameNotificationService.attack(
              gameState.lastAction.attacker,
              gameState.lastAction.defender,
              gameState.lastAction.damage
            );
          }
          break;
        case 'SUMMON':
          if (cardName) {
            gameNotificationService.summon(cardName, byOpponent);
          }
          break;
        case 'SPELL_CAST':
        case 'SPELL_ACTIVATION':
          if (cardName) {
            gameNotificationService.spell(cardName, byOpponent);
          }
          break;
        case 'TRAP_SET':
        case 'SET_TRAP':
          gameNotificationService.trapSet(byOpponent);
          break;
        case 'EFFECT_APPLIED':
          if (cardName && gameState.lastAction.effectType) {
            gameNotificationService.activateEffect(
              cardName,
              gameState.lastAction.message
            );
          }
          break;
        case 'DRAW':
          if (gameState.lastAction.player === 'player') {
            gameNotificationService.drawCard();
          }
          break;
        case 'PHASE_CHANGE':
          if (gameState && gameState.activePlayer === 'player' && gameState.phase === 'draw') {
            gameNotificationService.yourTurn();
          }
          break;
        case 'GAME_OVER':
          if (gameState.lastAction.winner === 'player') {
            gameNotificationService.victory();
          } else {
            gameNotificationService.defeat();
          }
          break;
        default:
          break;
      }
    }
  }, [gameState?.lastAction]);

  // Shi-fu-mi : garder l'écran de résultat visible ~2,4s (la phase passe
  // immédiatement à mulligan côté serveur), et réarmer pour la partie suivante.
  // ⚠️ Garde par SIGNATURE (chaîne stable) : l'objet rps est re-cloné à chaque
  // broadcast → un useEffect sur son identité annulerait le timer à chaque
  // re-rendu et l'overlay ne serait jamais refermé.
  const rpsResultSig = gameState?.rps?.result
    ? `${gameState.rps.result.winner}|${gameState.rps.result.playerChoice}|${gameState.rps.result.opponentChoice}|${gameState.rps.ties || 0}`
    : null;

  useEffect(() => {
    if (!rpsResultSig) {
      // Égalité (re-jeu) ou nouvelle partie → réarmer l'affichage
      rpsDismissedSigRef.current = null;
      setRpsDismissed(false);
      return;
    }
    if (rpsDismissedSigRef.current === rpsResultSig) return; // déjà traité
    const t = setTimeout(() => {
      rpsDismissedSigRef.current = rpsResultSig;
      setRpsDismissed(true);
    }, 2400);
    return () => clearTimeout(t);
  }, [rpsResultSig]);

  // Choix shi-fu-mi → serveur (l'IA répond immédiatement)
  const handleRpsChoice = useCallback((choice) => {
    performGameAction('RPS_CHOICE', { choice })
      .then(() => refreshGameState())
      .catch(err => console.error('[BATTLEFIELD] Erreur RPS_CHOICE:', err));
  }, [performGameAction, refreshGameState]);

  // Rafraîchissement périodique
  useEffect(() => {
    if (currentGame && !gameState && serverConnectAttempts < 3) {
      const timer = setTimeout(() => {
        console.log("[BATTLEFIELD] Tentative de rafraîchissement de l'état...");
        refreshGameState().catch(err => {
          console.error("[BATTLEFIELD] Échec du rafraîchissement:", err);
          setServerConnectAttempts(prev => prev + 1);
          if (serverConnectAttempts >= 2) {
            setConnectionError("Impossible de se connecter au serveur après plusieurs tentatives.");
          }
        });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [currentGame, gameState, refreshGameState, serverConnectAttempts]);

  // Toasts pilotés par le JOURNAL (append-only) : couvre les événements
  // sans lastAction exploitable — pièges qui se déclenchent (une carte
  // pouvait « disparaître » sans explication), destructions, shi-fu-mi,
  // annulations de déclaration. Index-based : rien n'est rejoué au montage.
  useEffect(() => {
    const log = gameState?.log;
    if (!Array.isArray(log)) return;

    if (lastLogIndexRef.current === null || lastLogIndexRef.current > log.length) {
      // Premier état vu (montage/refresh) ou nouveau log plus court (nouvelle partie)
      lastLogIndexRef.current = log.length;
      return;
    }

    for (let i = lastLogIndexRef.current; i < log.length; i++) {
      const entry = log[i];
      if (!entry || !entry.type) continue;

      const byOpponent = entry.player === 'opponent';

      switch (entry.type) {
        case 'RPS_START':
          gameNotificationService.rpsStart();
          break;
        case 'RPS_TIE':
          gameNotificationService.rpsTie();
          break;
        case 'RPS_RESULT':
          gameNotificationService.rpsResult(entry.player === 'player');
          break;
        case 'TRAP_ACTIVATED': {
          const name = (entry.message.match(/Piège activé\s*:\s*(.+?)!/) || [])[1] || 'Piège';
          gameNotificationService.trapActivated(name, byOpponent);
          break;
        }
        case 'DESTROY': {
          const name = (entry.message.match(/^(.+?) est détruit/) || [])[1] || 'Un monstre';
          gameNotificationService.cardDestroyed(name, entry.owner === 'player');
          // Animation : la carte détruite glisse vers le cimetière de son
          // propriétaire (fantôme depuis le centre du plateau).
          try {
            const owner = entry.owner || entry.player || 'player';
            const gyEl = document.querySelector(`[data-graveyard="${owner}"]`);
            const frameEl = document.querySelector('.battlefield-frame');
            if (gyEl && frameEl) {
              const fr = frameEl.getBoundingClientRect();
              const gr = gyEl.getBoundingClientRect();
              const ghost = document.createElement('div');
              ghost.className = 'destroyed-card-ghost';
              ghost.textContent = '☠';
              document.body.appendChild(ghost);
              animateFlight(ghost, {
                from: { x: fr.left + fr.width / 2, y: fr.top + fr.height * 0.45 },
                to: { x: gr.left + gr.width / 2, y: gr.top + gr.height / 2 },
                velocity: { x: 0, y: 0 },
                rotation: { from: 0, to: 220 },
                spin: 0,
                scale: { from: 1.1, to: 0.55 },
                duration: 850,
                onLand: () => {
                  gyEl.classList.add('graveyard-flash');
                  setTimeout(() => gyEl.classList.remove('graveyard-flash'), 500);
                  ghost.remove();
                },
              });
            }
          } catch (e) { /* animation décorative — ne jamais casser le flux */ }
          break;
        }
        case 'UNDECLARE_ATTACK':
        case 'UNDECLARE_DEFEND': {
          const name = (entry.message.match(/^(.+?) reprend/) || [])[1] || 'Un monstre';
          gameNotificationService.undeclare(name, entry.type === 'UNDECLARE_DEFEND');
          break;
        }
        case 'REACT_WINDOW':
          if (entry.player === 'player') {
            gameNotificationService.gameEvent(
              "L'adversaire a défendu — jouez une magie rapide ou continuez",
              'info'
            );
          }
          break;
        case 'STACK_RESOLVED':
          if (entry.player === 'player') {
            gameNotificationService.gameEvent('Pile résolue — continuez ou confirmez le combat', 'info');
          }
          break;
        case 'ERROR':
          // Seulement les erreurs du joueur local (l'IA peut en produire)
          if (entry.player === 'player' && entry.message) {
            showError(entry.message);
          }
          break;
        default:
          break;
      }
    }

    lastLogIndexRef.current = log.length;
  }, [gameState?.log, showError]);

  // Alertes d'erreur
  useEffect(() => {
    if (gameError) {
      showError(gameError);
    }
  }, [gameError, showError]);

  // Nettoyage lors du démontage
  useEffect(() => {
    return () => {
      const isNavigatingAway = !document.querySelector('.battlefield-container');
      if (isNavigatingAway) {
        console.log("[BATTLEFIELD] Navigation complète hors de la page, nettoyage de la partie");
        leaveGame();
      }
    };
  }, [leaveGame]);

  // Log pour voir l'état au moment du drag
  useEffect(() => {
    if (draggingCard) {
      console.log("[BATTLEFIELD] Carte en cours de drag:", draggingCard);
      console.log("[BATTLEFIELD] État actuel:", {
        phase: gameState?.phase,
        activePlayer: gameState?.activePlayer,
        playerHasAttack: gameState?.playerHasAttack,
        opponentHasAttack: gameState?.opponentHasAttack,
        pendingAttack: gameState?.pendingAttack,
        isDefenseMode: gameState?.activePlayer === 'player' && 
                      !gameState?.playerHasAttack && 
                      gameState?.opponentHasAttack
      });
    }
  }, [draggingCard, gameState]);

  // Démarrer une partie contre l'IA
  const handleStartAIGame = async () => {
    if (!selectedDeckId) {
      showError("Veuillez sélectionner un deck.");
      return;
    }
    
    setIsStartingGame(true);
    setConnectionError(null);
    
    try {
      console.log("[DEBUG_BATTLEFIELD] Début création partie IA avec deck:", selectedDeckId);
      
      const result = await createNewGame(selectedDeckId, { 
        isAI: true, 
        difficulty: 'medium' 
      });
      
      if (!result || !result.gameId) {
        throw new Error("Création de partie échouée: aucun ID de partie reçu");
      }
      
      showSuccess("Partie IA créée avec succès!");
      setShowDeckSelector(false);
      
      setTimeout(() => {
        if (currentGame && !gameState) {
          refreshGameState();
        }
      }, 1000);
    } catch (err) {
      console.error("[DEBUG] Error creating AI game:", err);
      
      if (err.message && err.message.includes('Network Error')) {
        setConnectionError("Impossible de se connecter au serveur. Vérifiez que le serveur est en cours d'exécution sur le port 3001.");
      } else {
        showError("Erreur lors de la création de la partie contre l'IA: " + (err.message || "Erreur inconnue"));
      }
    } finally {
      setIsStartingGame(false);
    }
  };

  // Rejoindre une partie existante
  const handleJoinExistingGame = async () => {
    if (!selectedDeckId) {
      showError("Veuillez sélectionner un deck.");
      return;
    }
    
    if (!gameId || gameId === 'ai' || gameId === 'undefined') {
      showError("ID de partie invalide.");
      return;
    }
    
    setIsStartingGame(true);
    setConnectionError(null);
    
    try {
      const result = await joinExistingGame(gameId, selectedDeckId);
      
      if (result && (result.success || result.offline)) {
        setShowDeckSelector(false);
        
        setTimeout(() => {
          if (currentGame) {
            refreshGameState().catch(err => {
              console.error("[BATTLEFIELD] Échec du rafraîchissement forcé:", err);
            });
          } else {
            console.warn("[BATTLEFIELD] Impossible de rafraîchir - pas de partie active");
          }
        }, 1500);
      } else {
        console.warn("[BATTLEFIELD] Résultat inattendu de joinExistingGame:", result);
      }
    } catch (err) {
      console.error("[BATTLEFIELD] Erreur lors de la connexion à la partie:", err);
      showError("Impossible de rejoindre la partie: " + (err.message || "Erreur inconnue"));
    } finally {
      setIsStartingGame(false);
    }
  };

  // Retour au matchmaking
  const handleReturnToMatchmaking = () => {
    leaveGame();
    navigate('/game/matchmaking');
  };

  // Fonction pour nettoyer manuellement les monstres en zone de bataille
  const cleanupBattleZone = useCallback(() => {
    if (!gameState) return;
    
    // Vérifier s'il y a des monstres en zone de bataille
    const playerBattleMonsters = gameState.playerField.filter(card => card.zone === 'battle');
    const opponentBattleMonsters = gameState.opponentField.filter(card => card.zone === 'battle');
    
    // Ne pas nettoyer s'il y a une attaque en cours ou une action en attente
    if (gameState.pendingAttack || gameState.pendingAction) {
      console.log("[BATTLEFIELD] Combat ou action en cours, pas de nettoyage");
      return;
    }
    
    // S'il y a des monstres en zone de bataille mais pas d'attaque en cours, forcer le nettoyage
    if ((playerBattleMonsters.length > 0 || opponentBattleMonsters.length > 0)) {
      console.log("[BATTLEFIELD] Nettoyage manuel des zones de bataille");
      
      // Appeler END_ATTACK pour nettoyer
      performGameAction('END_ATTACK')
        .then(response => {
          console.log("[BATTLEFIELD] Nettoyage terminé");
          refreshGameState();
        })
        .catch(error => {
          console.error("[BATTLEFIELD] Erreur lors du nettoyage:", error);
          // Dernier recours: actualiser l'état du jeu
          refreshGameState();
        });
    }
  }, [gameState, performGameAction, refreshGameState]);
  
  // Ajouter un effet pour nettoyer automatiquement
  useEffect(() => {
    if (gameState) {
      const playerBattleMonsters = gameState.playerField.filter(card => card.zone === 'battle');
      const opponentBattleMonsters = gameState.opponentField.filter(card => card.zone === 'battle');
      
      // Ne pas nettoyer si nous sommes en phase de bataille ou s'il y a une attaque/action en cours
      if (gameState.phase !== 'battle' && 
          !gameState.pendingAttack && 
          !gameState.pendingAction && 
          (playerBattleMonsters.length > 0 || opponentBattleMonsters.length > 0)) {
        console.log("[BATTLEFIELD] Détection de monstres bloqués en zone de bataille");
        
        // Attendre un court délai puis nettoyer
        const cleanupTimeout = setTimeout(() => {
          cleanupBattleZone();
        }, 500);
        
        return () => clearTimeout(cleanupTimeout);
      }
    }
  }, [gameState, cleanupBattleZone]);

  // Passer à la phase suivante
  const handleEndPhase = async () => {
    try {
      console.log("Tentative de changement de phase");
      
      // Utiliser notre état local au lieu de dépendre de gameLoading
      setPhaseChanging(true);
      
      const result = await performGameAction('END_PHASE');
      console.log(`Résultat de END_PHASE:`, result);
      
      // Si nous avons un résultat avec gameState, nous n'avons pas besoin de rafraîchir
      if (!result || !result.gameState) {
        // Seulement si nécessaire
        await refreshGameState();
      }
      
    } catch (error) {
      console.error("Erreur lors du changement de phase:", error);
      showError("Erreur lors du traitement de l'action: " + (error.message || "Erreur inconnue"));
    } finally {
      setPhaseChanging(false);
    }
  };

  // Fonction pour afficher un message d'aide contextuel pendant le combat
  const getBattleHelpMessage = () => {
    // Retourner simplement null pour supprimer tous les messages d'aide
    return null;
    
    // Tout le reste de la fonction peut être conservé mais ne sera jamais exécuté
    // car nous retournons null immédiatement
  };

  // Ajouter une animation CSS en ligne pour les messages d'aide
  const battleHelpStyles = `
    @keyframes defense-help-pulse {
      0% { opacity: 0.8; transform: translate(-50%, -50%) scale(0.98); }
      100% { opacity: 1; transform: translate(-50%, -50%) scale(1.02); }
    }
    
    @keyframes attack-help-pulse {
      0% { opacity: 0.8; transform: translate(-50%, -50%) scale(0.98); }
      100% { opacity: 1; transform: translate(-50%, -50%) scale(1.02); }
    }
  `;

  // Fonction pour valider une action ou passer
  // Fonction pour gérer l'action principale (confirmer, attaquer ou passer)
  const handleActionOrPass = async () => {
    try {
      console.log("[Battlefield] Tentative d'action ou passage");
      
      // Ajoutez ces logs pour déboguer l'état actuel
      console.log("[Battlefield] État actuel:", {
        phase: gameState?.phase,
        activePlayer: gameState?.activePlayer,
        pendingAction: gameState?.pendingAction,
        pendingAttack: gameState?.pendingAttack,
        playerHasAttack: gameState?.playerHasAttack,
        opponentHasAttack: gameState?.opponentHasAttack
      });
      
      // Si c'est le tour de l'adversaire, empêcher les actions du joueur
      if (gameState && gameState.activePlayer !== 'player') {
        console.log("[Battlefield] Tentative d'action pendant le tour de l'adversaire");
        showError("C'est au tour de l'adversaire de jouer");
        return;
      }
      
      // Si nous sommes en phase de mulligan, montrer l'écran de mulligan
      if (gameState && gameState.phase === 'mulligan') {
        setShowMulligan(true);
        return;
      }
      
      setPhaseChanging(true);
      
      // Si nous avons une action en attente à confirmer
      if (gameState && gameState.pendingAction) {
        console.log(`[Battlefield] Confirmation de l'action: ${gameState.pendingAction.type}`);
        
        // Pour la défense spécifiquement
        if (gameState.pendingAction.type === 'PREPARE_DEFENSE') {
          console.log(`[Battlefield] Confirmation de la défense`);
          
          try {
            // Confirmer la défense
            const result = await performGameAction('CONFIRM_ACTION');
            console.log(`Résultat de CONFIRM_ACTION pour défense:`, result);
            
            // Rafraîchir l'état pour s'assurer que tout est mis à jour
            if (!result || !result.gameState) {
              await refreshGameState();
            }
          } catch (error) {
            console.error("[Battlefield] Erreur lors de la confirmation de défense:", error);
            showError("Erreur lors de la confirmation de la défense");
          }
        } else {
          // Autres types d'actions en attente (existant)
          const result = await performGameAction('CONFIRM_ACTION');
          console.log(`Résultat de l'action CONFIRM_ACTION:`, result);
          
          // Rafraîchir l'état du jeu après la confirmation
          if (!result || !result.gameState) {
            await refreshGameState();
          }
        }
        
        return;
      }
      
      // Si nous avons une attaque en attente de résolution (après défense de l'IA)
      if (gameState && gameState.pendingAttack && 
          (!gameState.pendingAttack.defenders || gameState.pendingAttack.defenders.length === 0) &&
          !gameState.playerHasAttack && gameState.opponentHasAttack) {
      
        console.log(`[Battlefield] Confirmation de défense vide - attaque directe`);
      
        try {
          // Confirmer la défense vide pour déclencher l'attaque directe
          const result = await performGameAction('CONFIRM_ACTION');
          console.log(`Résultat de CONFIRM_ACTION pour défense vide:`, result);
        
          // Rafraîchir l'état pour s'assurer que tout est mis à jour
          if (!result || !result.gameState) {
            await refreshGameState();
          }
        } catch (error) {
          console.error("[Battlefield] Erreur lors de la confirmation de défense vide:", error);
          showError("Erreur lors de la confirmation de la défense");
        }
      
        return;
      }
        
      // les ramener à la zone de monstre normale et terminer la phase de bataille
      if (gameState && gameState.phase === 'battle' && 
          gameState.playerField.some(card => card.zone === 'battle')) {
    
        // Vérifier si nous sommes en défense
        const isDefenseMode = gameState.pendingAttack && 
                              gameState.pendingAttack.attackers && 
                              !gameState.playerHasAttack && 
                              gameState.opponentHasAttack;
                            
        if (isDefenseMode) {
          console.log(`[Battlefield] Confirmation de la défense`);
          // Utiliser CONFIRM_ACTION pour confirmer la défense
          const result = await performGameAction('CONFIRM_ACTION');
        } else {
          console.log(`[Battlefield] Confirmation de l'attaque`);
        
          // Au lieu d'utiliser END_ATTACK qui pourrait être interprété comme une annulation,
          // utiliser CONFIRM_ACTION pour confirmer l'attaque
          const result = await performGameAction('CONFIRM_ACTION');
        }
      
        return;
      }
      
      // Si nous sommes en phase de bataille, terminer la phase d'attaque
      if (gameState && gameState.phase === 'battle') {
        console.log(`[Battlefield] Fin de la phase d'attaque`);
        
        const result = await performGameAction('END_PHASE');
        console.log(`Résultat de l'action END_PHASE:`, result);
        
        // Rafraîchir l'état du jeu
        if (!result || !result.gameState) {
          await refreshGameState();
        }
        
        return;
      }
  
      // Dans tous les autres cas (phase principale), nous passons simplement
      console.log(`[Battlefield] Le joueur passe son tour`);
      
      const result = await performGameAction('PASS');
      console.log(`Résultat de l'action PASS:`, result);
      
      // Rafraîchir l'état du jeu
      if (!result || !result.gameState) {
        await refreshGameState();
      }
      
    } catch (error) {
      console.error("Erreur lors de l'action:", error);
      showError("Erreur lors du traitement: " + (error.message || "Erreur inconnue"));
    } finally {
      setPhaseChanging(false);
    }
  };  

  // Fonction pour obtenir une description de la phase actuelle
  const getPhaseDescription = () => {
    if (!gameState) return "";
    
    const isPlayerTurn = gameState.activePlayer === 'player';
        
    switch (gameState.phase) {
      case 'mulligan':
        return "Sélectionnez les cartes à remplacer dans votre main de départ.";
      case 'main':
        return isPlayerTurn 
          ? "Jouez des cartes, déplacez vos monstres pour attaquer, ou passez."
          : "L'adversaire joue son tour...";
      case 'battle':
        return isPlayerTurn 
          ? gameState.playerHasAttack 
            ? "Déclarez vos attaquants ou terminez la phase de bataille."
            : "Déclarez vos défenseurs contre les attaquants adverses."
          : "L'adversaire est en phase de bataille...";
      case 'end':
        return isPlayerTurn 
          ? "Fin de tour: Vos sorts et effets se résolvent. Cliquez pour terminer."
          : "L'adversaire termine son tour...";
      default:
        return "";
    }
  };

  // Gérer la nouvelle partie après une victoire/défaite contre l'IA
  // R34bb : réinitialisation EXPLICITE de tous les drapeaux locaux — la
  // navigation vers la même route ne remonte PAS le composant, et un état
  // résiduel (attache/reprise) laissait « En attente de l'état du jeu ».
  const handleRematch = () => {
    setGameResult(null);
    leaveGame();
    setShowResumeScreen(false);
    setAttaching(false);
    setIsStartingGame(false);
    // Afficher directement le sélecteur : la navigation vers la même route
    // ne remonte pas le composant (R34bb).
    setShowDeckSelector(true);
    navigate('/game/battle/ai', { replace: true });
  };

  // Gérer l'abandon d'une partie
  const handleSurrenderGame = () => {
    leaveGame();
    navigate('/game/matchmaking');
  };

  // Cinématique de démarrage active ? (partie IA, phase d'ouverture,
  // pas d'option « passer l'intro »).
  // ⚠ isAIGame vient du CONTEXTE (useGame) — le state diffusé par le
  // serveur ne contient PAS ce champ (game-update n'envoie que gameState),
  // l'ancien test gameState.isAIGame était donc toujours undefined et le
  // vortex ne se déclenchait jamais.
  // R34o : le vortex se joue aussi bien en IA qu'en JcJ.
  const vortexActive = !startVortexDone && gameState &&
    !gameState.isGameOver &&
    (gameState.phase === 'rps' || gameState.phase === 'mulligan') &&
    localStorage.getItem('elementumSkipVortex') !== '1';

  // Retourner le composant
  return (
    <div 
      ref={battlefieldRef}
      className="battlefield-container"
    >
      {/* Styles pour les animations d'aide */}
      <style dangerouslySetInnerHTML={{ __html: battleHelpStyles }} />

      {/* Arrière-plan vidéo (derrière tout) */}
      <div className="battlefield-video-background">
        <video autoPlay loop muted playsInline>
          <source src={battleboardVideo} type="video/mp4" />
        </video>
      </div>
      
      {/* Cadre qui contient tout le contenu du jeu avec un ratio fixe */}
      <div
        className={`battlefield-frame${devMode ? ' dev-mode' : ''}`}
        style={{
          '--font-scale': fontScale,
          '--fs-life': fontScales.life,
          '--fs-monster': fontScales.monster,
          '--fs-cost': fontScales.cost,
          '--fs-deck': fontScales.deck,
          '--fs-action': fontScales.action,
          '--fs-zone': fontScales.zone,
          /* Taille des gemmes de coût sur les cartes (réglage dev) */
          '--gem-scale': fontScales.gem ?? 1,
        }}
      >
        {/* Image d'arrière-plan superposée par-dessus la vidéo */}
        <div 
          className="battlefield-image-overlay"
          style={{ backgroundImage: `url(${battleBackground})` }}
        ></div>
    
        {/* Cinématique de démarrage : vortex + pioche des 4 cartes.
            Rendu via PORTAL sur <body> : le frame est transformé (scale/rotate
            pendant l'aspiration) et overflow:hidden — un enfant fixed y serait
            repositionné/clippé par le transformed ancestor. Sur body, l'overlay
            fixed couvre bien tout l'écran pendant toute l'animation. */}
        {vortexActive && createPortal(
          <StartVortex onComplete={() => setStartVortexDone(true)} />,
          document.body
        )}

        {/* Icône de réglages */}
        <div className="settings-icon" onClick={() => setShowSettings(!showSettings)}>
          ⚙️
        </div>
        
        {/* Afficher le menu de réglages */}
        {showSettings && (
          <SettingsMenu 
            isAdmin={isAdminUser}
            devMode={devMode}
            onToggleDevMode={toggleDevMode}
            onResetPositions={resetElementPositions}
            showGameLog={showGameLog}
            onToggleGameLog={() => setShowGameLog(!showGameLog)}
            onSurrenderGame={handleSurrenderGame}
            onClose={() => setShowSettings(false)}
          />
        )}
        
        {/* Mode développeur */}
        {devMode && (
          <DevPanel 
            elementPositions={elementPositions}
            elementSizes={elementSizes}
            updateElementPosition={(elementId, axis, value) => 
              updateElementPosition(elementId, axis, value, setElementPositions)}
            updateElementSize={(elementId, dimension, value) => 
              updateElementSize(elementId, dimension, value, setElementSizes)}
            adminSecret={adminSecret}
            updateAdminSecret={updateAdminSecret}
            onSave={() => handleDevElementDragEnd(elementPositions, elementSizes, saveToLocal)}
            onReset={() => resetElementPositions(setElementPositions)}
            onExportLayout={() => {
              const payload = {
                positions: elementPositions,
                sizes: elementSizes,
                fontScales,
                masterFontScale: fontScale,
              };
              const json = JSON.stringify(payload, null, 2);
              try { navigator.clipboard?.writeText(json); } catch (e) { /* optionnel */ }
              const blob = new Blob([json], { type: 'application/json' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = 'elementum-layout.json';
              a.click();
              URL.revokeObjectURL(a.href);
              showSuccess('Layout exporté (téléchargé + copié) — collez ces valeurs dans les defaults de useGameElements.js pour la prod', 'success', 6000);
            }}
            isCollapsed={isDevPanelCollapsed}
            onToggleCollapse={() => setIsDevPanelCollapsed(!isDevPanelCollapsed)}
            position={devPanelPosition}
            onDragStart={handleDevPanelDragStart}
            panelRef={devPanelRef}
            fontScale={fontScale}
            onUpdateFontScale={updateGlobalFontScale}
            fontScales={fontScales}
            onUpdateElementFontScale={updateElementFontScale}
            onResetFonts={resetFontScales}
          />
        )}
    
        {(!gameState && (gameLoading || isStartingGame) && !phaseChanging) ? (
          <LoadingScreen 
            error={connectionError}
            onRetry={() => {
              setConnectionError(null);
              setServerConnectAttempts(0);
              refreshGameState();
            }}
            onCancel={handleReturnToMatchmaking}
            isLoading={gameLoading || isStartingGame}
          />
        ) : gameState ? (
          // Si nous avons un état de jeu, afficher le plateau
          <>
            {/* Journal de jeu */}
            {showGameLog && (
              <div className="battlefield-log">
                <GameLog 
                  log={gameState?.log || []} 
                  onClose={() => setShowGameLog(false)}
                />
              </div>
            )}

            {/* Consultation d'un cimetière (le sien ou celui de l'adversaire —
                information publique) */}
            {graveyardView && gameState && (() => {
              const cards = (graveyardView === 'player'
                ? gameState.playerGraveyard
                : gameState.opponentGraveyard) || [];
              const idx = Math.min(gyIndex, Math.max(0, cards.length - 1));
              const card = cards[idx];
              const typeLabel = (t) => t === 'CHARACTER' ? 'Créature'
                : t === 'SPELL' ? 'Sort' : t === 'TRAP' ? 'Piège'
                : t === 'EQUIPMENT' ? 'Équipement' : t === 'TERRAIN' ? 'Terrain' : (t || '');
              return (
                <div
                  className="graveyard-modal-overlay"
                  onClick={() => setGraveyardView(null)}
                >
                  <div className="graveyard-modal" onClick={(e) => e.stopPropagation()}>
                    <h3>
                      ☠ Cimetière {graveyardView === 'player' ? '(vos cartes)' : '(adversaire)'}
                    </h3>
                    <div className="graveyard-modal-sub">{cards.length} carte(s)</div>
                    {cards.length === 0 ? (
                      <div className="graveyard-modal-empty">Aucune carte au cimetière.</div>
                    ) : (
                      <div className="graveyard-carousel">
                        <button
                          className="gy-arrow gy-arrow-left"
                          disabled={idx <= 0}
                          onClick={() => setGyIndex(idx - 1)}
                          aria-label="Carte précédente"
                        >‹</button>
                        <div
                          className="gy-card"
                          title="Voir le détail de la carte"
                          style={{ cursor: 'pointer', outline: 'none', borderRadius: '10px' }}
                          onClick={(e) => { e.stopPropagation(); setSelectedCard(card); }}
                        >
                          <img
                            className="gy-card-img"
                            src={card.imageUrl || `/api/static/images/cards/${getCardElement(card)}.svg`}
                            alt={card.name || 'Carte'}
                            onError={(e) => { e.target.src = '/api/static/images/card-backs/neutral.webp'; }}
                          />
                          <div className="gmc-name">{card.name || card.id}</div>
                          <div className="gmc-meta">
                            {(card.element || '')} · {typeLabel(card.type)} · {card.cost}◆
                          </div>
                          {card.type === 'CHARACTER' && (
                            <div className="gmc-stats">⚔ {card.attack ?? '—'} / 🛡 {card.defense ?? '—'}</div>
                          )}
                        </div>
                        <button
                          className="gy-arrow gy-arrow-right"
                          disabled={idx >= cards.length - 1}
                          onClick={() => setGyIndex(idx + 1)}
                          aria-label="Carte suivante"
                        >›</button>
                      </div>
                    )}
                    {cards.length > 0 && (
                      <div className="gy-counter">{idx + 1} / {cards.length}</div>
                    )}
                    <button
                      className="graveyard-modal-close"
                      onClick={() => setGraveyardView(null)}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              );
            })()}
                        {/* Indicateur de phase */}
            {gameState && gameState.pendingAction === 'react' && (
              <div className={`react-window-banner ${gameState.activePlayer === 'player' ? 'mine' : 'theirs'}`}>
                {gameState.activePlayer === 'player' ? (
                  <>⚡ L'adversaire a défendu — jouez une magie rapide (glissez-la sur le plateau) ou « Continuer »</>
                ) : (
                  <>⏳ L'adversaire envisage une magie rapide…</>
                )}
              </div>
            )}
            {/* Indicateur de tour : n'apparait qu'apres 20 s d'inactivite,
                barre de contour consommee en 10 s puis fin de tour auto.
                Masque si un detail de carte est ouvert (selectedCard). */}
            <TurnTimerIndicator
              gameState={gameState}
              isHidden={!!selectedCard}
              onAutoEndTurn={handleActionOrPass}
              getPhaseDescription={getPhaseDescription}
            />
            
            {/* Superposition du tour de l'adversaire — jamais par-dessus
                l'aperçu détaillé d'une carte (selectedCard) */}
            {gameState && gameState.activePlayer !== 'player' && !selectedCard && (
              <div className="opponent-turn-overlay" style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '20px',
                borderRadius: '10px',
                zIndex: 40,
                textAlign: 'center',
                border: '1px solid #ff5a5a',
                boxShadow: '0 0 15px #ff5a5a',
                pointerEvents: 'none', // Ne pas bloquer les interactions
                opacity: 0.8,
                transition: 'opacity 0.5s'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>Tour de l'adversaire</div>
                <div style={{ fontSize: '16px' }}>Veuillez patienter...</div>
              </div>
            )}
            
            {/* Shi-fu-mi avant mulligan — visible après l'animation de
                démarrage (vortex), pendant la phase rps, puis ~2,4s après le
                résultat pour l'animation de révélation */}
            {gameState && !vortexActive && gameState.rps && (gameState.phase === 'rps' || !rpsDismissed) && (
              <RpsScreen
                gameState={gameState}
                onChoose={handleRpsChoice}
              />
            )}

            {/* Phase de mulligan — jamais pendant la cinématique d'ouverture */}
            {gameState && !vortexActive && gameState.phase === 'mulligan' && (
              <MulliganScreen 
                cards={gameState.playerHand}
                onConfirm={handleMulliganConfirm}
                getCardElement={getCardElement}
              />
            )}

            {/* PvP : attente de l'adversaire pendant le mulligan */}
            {gameState && !vortexActive && gameState.phase === 'mulligan'
              && !isAIGame && gameState.playerMulliganDone && !gameState.opponentMulliganDone && (
              <div style={{
                position: 'fixed', top: '12%', left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(10, 6, 30, 0.85)', border: '1px solid rgba(120, 200, 255, 0.45)',
                borderRadius: '10px', padding: '10px 22px', zIndex: 700,
                color: '#cfe6ff', fontSize: '15px', letterSpacing: '1px',
                boxShadow: '0 0 18px rgba(60, 140, 255, 0.35)',
              }}>
                ⏳ En attente du mulligan de l'adversaire…
              </div>
            )}

            {/* PvP : ce n'est pas votre tour */}
            {gameState && !vortexActive && !isAIGame && gameState.phase === 'main'
              && gameState.activePlayer === 'opponent' && !gameState.isGameOver && (
              <div style={{
                position: 'fixed', top: '4%', left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(30, 8, 12, 0.8)', border: '1px solid rgba(255, 120, 120, 0.4)',
                borderRadius: '10px', padding: '8px 20px', zIndex: 700,
                color: '#ffd9d9', fontSize: '14px', letterSpacing: '1px',
                boxShadow: '0 0 16px rgba(255, 80, 80, 0.3)',
              }}>
                ⏳ Tour de l'adversaire…
              </div>
            )}
            
            {/* Message d'aide pour la bataille */}
            {gameState && getBattleHelpMessage()}
            
            <div className="battlefield-main">
              {/* Zone de jeu avec les éléments positionnés avec des pourcentages */}
              <EnhancedLifePoints 
                player="opponent"
                gameState={gameState}
                position={elementPositions.opponentLife}
                size={elementSizes.opponentLife}
                onDevDragStart={(e) => handleDevElementDragStart(e, "opponentLife")}
                onGemMove={makeGemMoveHandler("opponentLife")}
                devMode={devMode}
              />
              
              <EnhancedLifePoints 
                player="player"
                gameState={gameState}
                position={elementPositions.playerLife}
                size={elementSizes.playerLife}
                onDevDragStart={(e) => handleDevElementDragStart(e, "playerLife")}
                onGemMove={makeGemMoveHandler("playerLife")}
                devMode={devMode}
              />
              
              <DeckDisplay 
                player="opponent"
                gameState={gameState}
                position={elementPositions.opponentDeck}
                size={elementSizes.opponentDeck}
                onGraveyardOpen={() => setGraveyardView('opponent')}
                onDevDragStart={(e) => handleDevElementDragStart(e, "opponentDeck")}
                devMode={devMode}
              />
              
              {/* Terrain adverse — remplace l'ancien cimetière (même gabarit,
                  juste EN DESSOUS du deck) ; le cimetière est accessible via le deck */}
              <TerrainSlot
                player="opponent"
                gameState={gameState}
                position={{
                  x: elementPositions.opponentDeck.x,
                  y: elementPositions.opponentDeck.y + (elementSizes.opponentDeck.height * 9) / 16 + 0.5,
                }}
                size={elementSizes.opponentDeck}
                onCardClick={handleSelectCard}
                devMode={devMode}
              />
              
              <DeckDisplay 
                player="player"
                gameState={gameState}
                position={elementPositions.playerDeck}
                size={elementSizes.playerDeck}
                onGraveyardOpen={() => setGraveyardView('player')}
                onDevDragStart={(e) => handleDevElementDragStart(e, "playerDeck")}
                devMode={devMode}
              />
              
              {/* Terrain du joueur — remplace l'ancien cimetière (juste AU-DESSUS du deck) */}
              <TerrainSlot
                player="player"
                gameState={gameState}
                position={{
                  x: elementPositions.playerDeck.x,
                  y: elementPositions.playerDeck.y - (elementSizes.playerDeck.height * 9) / 16 - 0.5,
                }}
                size={elementSizes.playerDeck}
                devMode={devMode}
              />
              
              <PlayerHand 
                player="opponent"
                gameState={gameState}
                position={elementPositions.opponentHand}
                size={elementSizes.opponentHand}
                onCardClick={handleSelectCard}
                onCardDragStart={handleCardDragStart}
                onDevDragStart={(e) => handleDevElementDragStart(e, "opponentHand")}
                devMode={devMode}
                getCardElement={getCardElement}
              />
              
              <PlayerHand 
                player="player"
                gameState={gameState}
                position={elementPositions.playerHand}
                size={elementSizes.playerHand}
                onCardClick={handleSelectCard}
                onCardDragStart={handleCardDragStart}
                onDevDragStart={(e) => handleDevElementDragStart(e, "playerHand")}
                devMode={devMode}
                getCardElement={getCardElement}
              />
              
              <MonsterZone 
                player="opponent"
                abilityTargeting={!!abilityTargeting}
                gameState={gameState}
                position={elementPositions.opponentMonsters}
                size={elementSizes.opponentMonsters}
                onCardClick={handleSelectCard}
                onCardDragStart={handleCardDragStart}
                onZoneClick={handleCardDrop}
                onDevDragStart={(e) => handleDevElementDragStart(e, "opponentMonsters")}
                devMode={devMode}
                getCardElement={getCardElement}
              />
              
              <MonsterZone 
                player="player"
                abilityTargeting={!!abilityTargeting}
                gameState={gameState}
                position={elementPositions.playerMonsters}
                size={elementSizes.playerMonsters}
                onCardClick={handleSelectCard}
                onCardDragStart={handleCardDragStart}
                onZoneClick={handleCardDrop}
                onDevDragStart={(e) => handleDevElementDragStart(e, "playerMonsters")}
                devMode={devMode}
                getCardElement={getCardElement}
              />
  
              <BattleZone
                player="player"
                gameState={gameState}
                position={elementPositions.playerBattleZone}
                size={elementSizes.playerBattleZone}
                onZoneClick={(e, zone, targetInfo) => {
                  console.log("[Battlefield] Clic sur zone de bataille joueur", targetInfo);
                  
                  // Si nous sommes en train de glisser un monstre depuis le terrain
                  if (draggingCard && draggingCard.location === 'field' && draggingCard.zone === 'monster') {
                    // Si c'est une action de défense
                    if (targetInfo && targetInfo.type === 'defense') {
                      console.log("[Battlefield] Défense contre attaquant", targetInfo.attackerPosition);
                      handleCardDrop(e, 'battle', targetInfo);
                    } else {
                      // Sinon c'est une déclaration d'attaque
                      handleAttack(draggingCard);
                    }
                  } else {
                    // Comportement normal pour les clics sans drag
                    handleCardDrop(e, 'battle', targetInfo);
                  }
                }}
                onDevDragStart={(e) => handleDevElementDragStart(e, "playerBattleZone")}
                devMode={devMode}
                getCardElement={getCardElement}
                onCardDragStart={handleCardDragStart}
              />
              
              <BattleZone
                player="opponent"
                gameState={gameState}
                position={elementPositions.opponentBattleZone}
                size={elementSizes.opponentBattleZone}
                onZoneClick={(e, zone, targetInfo) => {
                  console.log("[Battlefield] Clic sur zone de bataille adversaire", targetInfo);
                  
                  // Si nous sommes en train de glisser un monstre
                  if (draggingCard && draggingCard.location === 'field') {
                    // Si le monstre est dans la zone de combat (déjà un attaquant)
                    if (draggingCard.zone === 'battle') {
                      console.log("[Battlefield] Attaque directe avec un monstre en zone de bataille");
                      handleCardDrop(e, 'battle', 'opponent');
                    } else if (draggingCard.zone === 'monster') {
                      // Sinon, c'est une déclaration d'attaque suivie d'une attaque directe
                      console.log("[Battlefield] Déclaration d'attaque puis attaque directe");
                      handleAttack(draggingCard, 'opponent');
                    }
                  } else {
                    // Pour les clics simples sans drag
                    handleCardDrop(e, 'battle', 'opponent');
                  }
                }}
                onDevDragStart={(e) => handleDevElementDragStart(e, "opponentBattleZone")}
                devMode={devMode}
                getCardElement={getCardElement}
              />
              <ActionButton 
                gameState={gameState}
                position={elementPositions.ActionButton}
                size={elementSizes.ActionButton}
                onAction={gameState?.phase === 'mulligan' ? () => setShowMulligan(true) : handleActionOrPass}
                loading={phaseChanging}
                onDevDragStart={(e) => handleDevElementDragStart(e, "ActionButton")}
                devMode={devMode}
              />
              
              {/* Prévisualisation de la carte sélectionnée */}
              {selectedCard && !draggingCard && (
                <BattlefieldCardPreview  
                  card={selectedCard}
                  onClose={() => setSelectedCard(null)}
                  getCardElement={getCardElement}
                  canActivate={
                    gameState?.activePlayer === 'player' &&
                    (gameState.playerField || []).some(c => c.instanceId === selectedCard.instanceId)
                  }
                  onActivateAbility={(card, abilityIndex, eff) => {
                    setSelectedCard(null);
                    const raw = eff.target || '';
                    const scope = raw.includes('ALLY') ? 'ally' : (raw.includes('ENEMY') ? 'enemy' : 'any');
                    setAbilityTargeting({ card, abilityIndex, scope });
                  }}
                />
              )}
              {abilityTargeting && (
                <div className="r27-ability-banner">
                  <span>🎯 Choisissez la carte ciblée pour l'effet de <b>{abilityTargeting.card.name}</b></span>
                  <button onClick={() => setAbilityTargeting(null)}>Annuler</button>
                </div>
              )}
            </div>
          </>
        ) : showResumeScreen ? (
          // R27 : une partie persistée est en attente (refresh en plein duel)
          <ResumeScreen
            onRejoin={async () => {
              setIsRejoining(true);
              setResumeError(null);
              try {
                const ok = await resumeActiveGame();
                if (ok) {
                  setShowResumeScreen(false);
                } else {
                  setResumeError('Partie introuvable ou terminée côté serveur.');
                }
              } catch (e) {
                setResumeError('Impossible de reprendre la partie : ' + (e.message || e));
              } finally {
                setIsRejoining(false);
              }
            }}
            onAbandon={() => {
              leaveGame();
              setShowResumeScreen(false);
              setShowDeckSelector(true);
            }}
            error={resumeError}
            isRejoining={isRejoining}
          />
        ) : showDeckSelector ? (
          // Sélecteur de deck (seulement si pas d'état de jeu)
          <DeckSelector 
            gameId={gameId}
            selectedDeckId={selectedDeckId}
            onDeckSelect={(deckId) => {
              setSelectedDeckId(deckId);
              try { localStorage.setItem('elementum_preferred_deck', deckId); } catch (e) { /* ok */ }
            }}
            onStartGame={gameId === 'ai' ? handleStartAIGame : handleJoinExistingGame}
            onCancel={handleReturnToMatchmaking}
            isStartingGame={isStartingGame}
            error={gameError}
          />
        ) : (
          // Écran d'attente par défaut
          <LoadingScreen 
            error={null}
            onRetry={() => refreshGameState()}
            onCancel={handleReturnToMatchmaking}
            isLoading={false}
          />
        )}
        
        {/* Afficher le résultat de la partie en superposition si nécessaire */}
        {isTutorialDuel ? <DuelCoach gameState={gameState} /> : null}
        <GameResult 
          result={gameResult}
          rewards={gameRewards}
          gameId={currentGame}
          victoryImage={victoryImage}
          defeatImage={defeatImage}
          isAIGame={isAIGame}
          onRematch={handleRematch}
          onQuit={handleSurrenderGame}
        />
      </div>
    </div>
  );
};

export default Battlefield;